import { CheckBadgeIcon } from "@heroicons/react/24/solid"
import React, { useState, useEffect, useRef } from "react"

import SocialProofNotificationCard from "../../../components/SocialProofNotificationCard"
import { urqlClient } from "../../../utils/utils"

const DISPLAY_DURATION_MS = 6000
const EXIT_ANIMATION_MS = 500
const SOCIAL_PROOF_HIDE_EVENT = "appointmentSocialProofNotification:hide"
const SOCIAL_PROOF_DISMISS_ALL_EVENT = "socialProofNotifications:dismissAll"

const LATEST_APPOINTMENTS_QUERY = `
  query LatestAppointments($lastNHours: Int!, $limit: Int!) {
    latestAppointments(lastNHours: $lastNHours, limit: $limit) {
      id
      clientDisplayName
      practice {
        user {
          name
          slug
          profilePhotos {
            small {
              any
              jpeg
              webp
            }
          }
        }
      }
      createdAt
    }
  }
`

const LATEST_APPOINTMENTS_LIMIT = 20
const LATEST_APPOINTMENTS_LAST_N_HOURS = 48
const FALLBACK_AVATAR_URL = "/images/icons/heal_me.svg"

const formatTimeDifference = (createdAt: string) => {
  const ts = Date.parse(createdAt)
  if (Number.isNaN(ts)) return "recently"
  const totalMinutes = Math.floor((Date.now() - ts) / 60_000)
  if (totalMinutes < 1) return "just now"
  if (totalMinutes < 60) return `${totalMinutes} min ago`
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (minutes === 0) return `${hours} hr ago`
  return `${hours} hr ${minutes} min ago`
}

interface LatestAppointmentsQueryData {
  id: string
  clientDisplayName: string
  practice: {
    user: {
      name: string
      slug: string
      profilePhotos: {
        small: {
          jpeg: string
          webp: string
        }
      }
    }
  }
  createdAt: string
}

const AppointmentConversionProofNotification = ({ isMobile }: { isMobile: boolean }) => {
  const [latestAppointments, setLatestAppointments] = useState<Array<LatestAppointmentsQueryData>>([])
  const [currentIndex, setCurrentIndex] = useState<number>(0)
  const [isCurrentIndexVisible, setIsCurrentIndexVisible] = useState<boolean>(false)
  const [socialProofHideInfo, setSocialProofHideInfo] = useState<{ hiddenAt: number; exitMs: number } | null>(null)
  const [dismissed, setDismissed] = useState<boolean>(false)

  const initialShowRef = useRef<NodeJS.Timeout | null>(null)
  const hideNotificationRef = useRef<NodeJS.Timeout | null>(null)
  const changeIndexRef = useRef<NodeJS.Timeout | null>(null)
  const hasHandledSocialProofHandoffRef = useRef(false)

  useEffect(() => {
    if (typeof document === "undefined") return

    const onSocialProofHide = (event: Event) => {
      const customEvent = event as CustomEvent<{ hiddenAt?: number; exitMs?: number }>
      const hiddenAt = typeof customEvent.detail?.hiddenAt === "number" ? customEvent.detail.hiddenAt : Date.now()
      const exitMs = typeof customEvent.detail?.exitMs === "number" ? customEvent.detail.exitMs : EXIT_ANIMATION_MS
      setSocialProofHideInfo({ hiddenAt, exitMs })
    }

    document.addEventListener(SOCIAL_PROOF_HIDE_EVENT, onSocialProofHide as EventListener)
    return () => {
      document.removeEventListener(SOCIAL_PROOF_HIDE_EVENT, onSocialProofHide as EventListener)
    }
  }, [])

  useEffect(() => {
    if (isCurrentIndexVisible) hasHandledSocialProofHandoffRef.current = true
  }, [isCurrentIndexVisible])

  useEffect(() => {
    if (dismissed) return
    let isCancelled = false

    const fetchLatestAppointments = async () => {
      try {
        const result = await urqlClient
          .query(LATEST_APPOINTMENTS_QUERY, {
            lastNHours: LATEST_APPOINTMENTS_LAST_N_HOURS,
            limit: LATEST_APPOINTMENTS_LIMIT
          })
          .toPromise()
        if (isCancelled) return
        const raw = result.data?.latestAppointments || []
        // Only show appointments with valid practice/user so we never crash on null profilePhotos
        const appointments = raw.filter(
          (a: LatestAppointmentsQueryData) => a?.practice?.user != null && a?.createdAt != null
        )
        setLatestAppointments(appointments)
        setCurrentIndex(0)
        if (appointments.length > 0) {
          // Await AppointmentSocialProofNotification to go invisible
          // base time = 6000 + 2000 safety margin
          if (initialShowRef.current) clearTimeout(initialShowRef.current)
          initialShowRef.current = setTimeout(() => setIsCurrentIndexVisible(true), 8000)
        }
      } catch (e: unknown) {
        console.error("Error fetching latest appointments:", e) // eslint-disable-line no-console
      }
    }

    fetchLatestAppointments()
    return () => {
      isCancelled = true
    }
  }, [dismissed])

  useEffect(() => {
    if (hasHandledSocialProofHandoffRef.current) return
    if (dismissed) return
    if (!socialProofHideInfo) return
    if (latestAppointments.length === 0) return
    if (isCurrentIndexVisible) return

    const targetTime = socialProofHideInfo.hiddenAt + socialProofHideInfo.exitMs
    const delay = Math.max(0, targetTime - Date.now())

    if (initialShowRef.current) clearTimeout(initialShowRef.current)
    initialShowRef.current = setTimeout(() => setIsCurrentIndexVisible(true), delay)
    hasHandledSocialProofHandoffRef.current = true
  }, [socialProofHideInfo, latestAppointments.length, isCurrentIndexVisible])

  const clearDisplayTimers = () => {
    if (initialShowRef.current) clearTimeout(initialShowRef.current)
    if (hideNotificationRef.current) clearTimeout(hideNotificationRef.current)
    initialShowRef.current = null
    hideNotificationRef.current = null
  }

  const clearAllTimers = () => {
    clearDisplayTimers()
    if (changeIndexRef.current) clearTimeout(changeIndexRef.current)
    changeIndexRef.current = null
  }

  const dismissAllNotifications = () => {
    setCurrentIndex(0)
    setIsCurrentIndexVisible(false)
    setDismissed(true)
    clearAllTimers()
  }

  const dismissNotifications = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    dismissAllNotifications()
  }

  useEffect(() => {
    return () => {
      clearAllTimers()
    }
  }, [])

  useEffect(() => {
    if (dismissed || !isCurrentIndexVisible) return

    hideNotificationRef.current = setTimeout(() => {
      setIsCurrentIndexVisible(false)

      if (changeIndexRef.current) clearTimeout(changeIndexRef.current)
      changeIndexRef.current = setTimeout(() => {
        setCurrentIndex((prev) => (prev < latestAppointments.length - 1 ? prev + 1 : 0))
        setIsCurrentIndexVisible(true)
      }, EXIT_ANIMATION_MS)
    }, DISPLAY_DURATION_MS)

    return () => {
      // Don't clear `changeIndexRef` here; it's responsible for advancing the index
      // after the exit animation finishes.
      if (hideNotificationRef.current) clearTimeout(hideNotificationRef.current)
      hideNotificationRef.current = null
    }
  }, [dismissed, isCurrentIndexVisible, latestAppointments.length])

  useEffect(() => {
    if (typeof document === "undefined") return

    const onDismissAll = () => {
      dismissAllNotifications()
    }

    document.addEventListener(SOCIAL_PROOF_DISMISS_ALL_EVENT, onDismissAll as EventListener)
    return () => {
      document.removeEventListener(SOCIAL_PROOF_DISMISS_ALL_EVENT, onDismissAll as EventListener)
    }
  }, [])

  if (dismissed || latestAppointments.length === 0) return null

  const appointment = latestAppointments[currentIndex]
  if (!appointment?.practice?.user) return null
  const profilePhoto = appointment.practice.user.profilePhotos?.small
  const rawImagePath = profilePhoto?.jpeg || profilePhoto?.webp
  const imageUrl = rawImagePath
    ? rawImagePath.startsWith("http")
      ? rawImagePath
      : `https://cdn.filestackcontent.com/${rawImagePath}`
    : FALLBACK_AVATAR_URL
  const proSlug = appointment.practice.user.slug ?? ""
  const proUrl = proSlug ? `https://heal.me/${proSlug}` : "https://heal.me/"
  const proName = appointment.practice.user.name ?? "a professional"
  const clientName = appointment.clientDisplayName ?? "Someone"
  const createdAt = appointment.createdAt ?? new Date().toISOString()

  return (
    <SocialProofNotificationCard
      imageUrl={imageUrl}
      isMobile={isMobile}
      visible={isCurrentIndexVisible}
      closeNotification={dismissNotifications}>
      <p className="font-manrope text-[15px] font-extrabold leading-[20px] text-[#30363f]">
        {clientName} booked a session
      </p>
      <p className="mt-1 font-manrope text-[15px] font-medium leading-[20px] text-[#6b7280]">
        with{" "}
        <a href={proUrl} className="font-semibold text-[#30363f] hover:underline" onClick={(e) => e.stopPropagation()}>
          {proName}
        </a>
      </p>
      <div className="mt-[2px] flex items-center justify-between gap-2">
        <p className="font-manrope text-[12px] font-medium leading-[16px] text-[#8f96a3]">
          {formatTimeDifference(createdAt)}
        </p>
        <div className="flex items-center gap-1 whitespace-nowrap text-[12px] font-semibold leading-[16px] text-[#1a77db]">
          <CheckBadgeIcon className="h-[14px] w-[14px]" />
          <a
            href="https://useproof.com/"
            target="_blank"
            rel="noreferrer noopener"
            onClick={(e) => e.stopPropagation()}>
            verified by Proof
          </a>
        </div>
      </div>
    </SocialProofNotificationCard>
  )
}

export default AppointmentConversionProofNotification
